import {
    HttpClient,
    HttpRequestError,
    HttpRequestMeta,
    useLoginState,
    viewRequests
} from '@esg/framework';
import { MutationOptions, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLogout } from './useLogout';

interface HttpCommandOptions<TRequest, TResponse> extends MutationOptions<TResponse, Error, TRequest> {
    readonly showNotificationError?: boolean;
}

export const useHttpCommand = <TRequest, TResponse>(requestMeta: HttpRequestMeta<TRequest, TResponse>, options?: HttpCommandOptions<TRequest, TResponse>) => {
    const { t } = useTranslation();
    const logout = useLogout();
    const [loginInfo] = useLoginState();
    const showNotificationError = options?.showNotificationError ?? true;

    const baseUrl = requestMeta.baseUrl();

    if (!baseUrl) {
        throw new Error('Base URL is null');
    }

    const query = useMutation<TResponse, Error, TRequest>({
        mutationKey: [requestMeta.method, baseUrl, requestMeta.path],
        mutationFn: async <TRequest>(requestData: TRequest) => {
            const httpClient = new HttpClient();

            if (requestMeta.authentication === 'bearer') {
                const accessToken = loginInfo?.token;
                if (!!accessToken) {
                    httpClient.setAuthorization('bearer', accessToken);
                }
            }

            try {
                return await httpClient.send<TResponse>({
                    ...requestMeta,
                    ...requestData,
                });
            } catch (error) {
                if (error instanceof Error && error.name === 'HttpRequestError') {
                    const requestError = error as HttpRequestError;

                    if (requestError.statusCode === 401) {
                        logout();
                        location.href = '/error/unauthorized';
                    }
                }

                if (showNotificationError) {
                    viewRequests.showNotification.send({
                        type: 'error',
                        title: (error as Error).name,
                        message: t((error as Error).message),
                    });
                }

                throw error;
            }
        },
        onSuccess: options?.onSuccess,
        onError: options?.onError,
    });

    return query;
};
